// NOTE: No longer exporting palettes synchronously to avoid blocking bundle load
// All palette access is now handled through lazy loading and Redux store

import type { ThemeConfig } from "@/types/theme";

// Palette loader functions - only load when needed
const paletteLoaders: Record<string, () => Promise<ThemeConfig>> = {
  // New Tailwind color palettes
  tailwindAmber: () => import("./amber").then((m) => m.tailwindAmber),
  tailwindBlue: () => import("./blue").then((m) => m.tailwindBlue),
  tailwindCyan: () => import("./cyan").then((m) => m.tailwindCyan),
  tailwindEmerald: () => import("./emerald").then((m) => m.tailwindEmerald),
  tailwindFuchsia: () => import("./fuchsia").then((m) => m.tailwindFuchsia),
  tailwindGray: () => import("./gray").then((m) => m.tailwindGray),
  tailwindGreen: () => import("./green").then((m) => m.tailwindGreen),
  tailwindIndigo: () => import("./indigo").then((m) => m.tailwindIndigo),
  tailwindLime: () => import("./lime").then((m) => m.tailwindLime),
  tailwindNeutral: () => import("./neutral").then((m) => m.tailwindNeutral),
  tailwindOrange: () => import("./orange").then((m) => m.tailwindOrange),
  tailwindPink: () => import("./pink").then((m) => m.tailwindPink),
  tailwindPurple: () => import("./purple").then((m) => m.tailwindPurple),
  tailwindRed: () => import("./red").then((m) => m.tailwindRed),
  tailwindRose: () => import("./rose").then((m) => m.tailwindRose),
  tailwindSky: () => import("./sky").then((m) => m.tailwindSky),
  tailwindSlate: () => import("./slate").then((m) => m.tailwindSlate),
  tailwindStone: () => import("./stone").then((m) => m.tailwindStone),
  tailwindTeal: () => import("./teal").then((m) => m.tailwindTeal),
  tailwindViolet: () => import("./violet").then((m) => m.tailwindViolet),
  tailwindYellow: () => import("./yellow").then((m) => m.tailwindYellow),
  tailwindZinc: () => import("./zinc").then((m) => m.tailwindZinc),
};

// Async function to load a specific palette
export const loadPalette = async (
  paletteName: string
): Promise<ThemeConfig | null> => {
  const loader = paletteLoaders[paletteName];
  if (!loader) {
    console.warn(
      `Palette "${paletteName}" not found. Available:`,
      Object.keys(paletteLoaders)
    );
    return null;
  }

  try {
    const palette = await loader();
    return palette;
  } catch (error) {
    console.error(`Failed to load palette "${paletteName}":`, error);
    return null;
  }
};

// Get available palette names
export const getAvailablePaletteNames = (): string[] => {
  return Object.keys(paletteLoaders);
};

// colorPalettes proxy is no longer needed since palettes are loaded directly into theme.classes
// If you need to access palette data, use loadPalette() function directly

// Export palette metadata for backward compatibility
export const paletteMetadata = {
  // modernBlue: {
  //   nameKey: "edit.general.theme.palettes.modernBlue.name",
  //   descriptionKey: "edit.general.theme.palettes.modernBlue.description",
  //   previewColors: [
  //     "from-blue-500 to-indigo-600",
  //     "from-purple-500 to-violet-600",
  //     "from-cyan-500 to-blue-500",
  //   ],
  //   hoverColor: "blue",
  // },
  // natureGreen: {
  //   nameKey: "edit.general.theme.palettes.natureGreen.name",
  //   descriptionKey: "edit.general.theme.palettes.natureGreen.description",
  //   previewColors: [
  //     "from-emerald-500 to-green-600",
  //     "from-teal-500 to-emerald-600",
  //     "from-lime-500 to-green-500",
  //   ],
  //   hoverColor: "green",
  // },
  // sunsetOrange: {
  //   nameKey: "edit.general.theme.palettes.sunsetOrange.name",
  //   descriptionKey: "edit.general.theme.palettes.sunsetOrange.description",
  //   previewColors: [
  //     "from-orange-500 to-red-500",
  //     "from-amber-500 to-orange-600",
  //     "from-yellow-500 to-amber-500",
  //   ],
  //   hoverColor: "orange",
  // },
  // royalPurple: {
  //   nameKey: "edit.general.theme.palettes.royalPurple.name",
  //   descriptionKey: "edit.general.theme.palettes.royalPurple.description",
  //   previewColors: [
  //     "from-purple-600 to-violet-700",
  //     "from-indigo-600 to-purple-600",
  //     "from-fuchsia-500 to-purple-600",
  //   ],
  //   hoverColor: "purple",
  // },
  // monochrome: {
  //   nameKey: "edit.general.theme.palettes.monochrome.name",
  //   descriptionKey: "edit.general.theme.palettes.monochrome.description",
  //   previewColors: [
  //     "from-gray-900 to-gray-700",
  //     "from-gray-600 to-gray-800",
  //     "from-slate-500 to-gray-600",
  //   ],
  //   hoverColor: "gray",
  // },
  // oceanTeal: {
  //   nameKey: "edit.general.theme.palettes.oceanTeal.name",
  //   descriptionKey: "edit.general.theme.palettes.oceanTeal.description",
  //   previewColors: [
  //     "from-teal-600 to-cyan-600",
  //     "from-blue-600 to-teal-600",
  //     "from-sky-500 to-blue-600",
  //   ],
  //   hoverColor: "teal",
  // },
  // cherryRed: {
  //   nameKey: "edit.general.theme.palettes.cherryRed.name",
  //   descriptionKey: "edit.general.theme.palettes.cherryRed.description",
  //   previewColors: [
  //     "from-red-600 to-rose-600",
  //     "from-pink-600 to-red-600",
  //     "from-red-500 to-pink-500",
  //   ],
  //   hoverColor: "red",
  // },
  // goldenYellow: {
  //   nameKey: "edit.general.theme.palettes.goldenYellow.name",
  //   descriptionKey: "edit.general.theme.palettes.goldenYellow.description",
  //   previewColors: [
  //     "from-yellow-500 to-amber-600",
  //     "from-amber-500 to-yellow-600",
  //     "from-orange-400 to-yellow-500",
  //   ],
  //   hoverColor: "yellow",
  // },
  // deepPink: {
  //   nameKey: "edit.general.theme.palettes.deepPink.name",
  //   descriptionKey: "edit.general.theme.palettes.deepPink.description",
  //   previewColors: [
  //     "from-pink-600 to-rose-600",
  //     "from-fuchsia-500 to-pink-600",
  //     "from-pink-500 to-purple-500",
  //   ],
  //   hoverColor: "pink",
  // },
  // trainerBold: {
  //   nameKey: "edit.general.theme.palettes.trainerBold.name",
  //   descriptionKey: "edit.general.theme.palettes.trainerBold.description",
  //   previewColors: [
  //     "from-yellow-400 to-yellow-500",
  //     "from-gray-800 to-black",
  //     "from-yellow-300 to-yellow-400",
  //   ],
  //   hoverColor: "yellow",
  // },
  // bellaVistaWarm: {
  //   nameKey: "edit.general.theme.palettes.bellaVistaWarm.name",
  //   descriptionKey: "edit.general.theme.palettes.bellaVistaWarm.description",
  //   previewColors: [
  //     "from-amber-700 to-orange-700",
  //     "from-amber-800 to-amber-900",
  //     "from-amber-500 to-yellow-500",
  //   ],
  //   hoverColor: "amber",
  // },
  // cloudflowBlue: {
  //   nameKey: "edit.general.theme.palettes.cloudflowBlue.name",
  //   descriptionKey: "edit.general.theme.palettes.cloudflowBlue.description",
  //   previewColors: [
  //     "from-sky-500 to-blue-600",
  //     "from-blue-600 to-indigo-600",
  //     "from-cyan-500 to-sky-500",
  //   ],
  //   hoverColor: "sky",
  // },
  // cryptoVault: {
  //   nameKey: "edit.general.theme.palettes.cryptoVault.name",
  //   descriptionKey: "edit.general.theme.palettes.cryptoVault.description",
  //   previewColors: [
  //     "from-violet-600 to-purple-700",
  //     "from-purple-700 to-indigo-800",
  //     "from-indigo-600 to-violet-600",
  //   ],
  //   hoverColor: "violet",
  // },
  // dreamhomesProfessional: {
  //   nameKey: "edit.general.theme.palettes.dreamhomesProfessional.name",
  //   descriptionKey:
  //     "edit.general.theme.palettes.dreamhomesProfessional.description",
  //   previewColors: [
  //     "from-slate-700 to-slate-800",
  //     "from-slate-800 to-gray-900",
  //     "from-gray-700 to-slate-700",
  //   ],
  //   hoverColor: "slate",
  // },
  // learnhubIndigo: {
  //   nameKey: "edit.general.theme.palettes.learnhubIndigo.name",
  //   descriptionKey: "edit.general.theme.palettes.learnhubIndigo.description",
  //   previewColors: [
  //     "from-indigo-600 to-purple-600",
  //     "from-purple-600 to-violet-700",
  //     "from-violet-600 to-indigo-600",
  //   ],
  //   hoverColor: "indigo",
  // },
  // medicareTeal: {
  //   nameKey: "edit.general.theme.palettes.medicareTeal.name",
  //   descriptionKey: "edit.general.theme.palettes.medicareTeal.description",
  //   previewColors: [
  //     "from-teal-600 to-cyan-700",
  //     "from-cyan-600 to-blue-700",
  //     "from-teal-500 to-teal-600",
  //   ],
  //   hoverColor: "teal",
  // },
  // powerfitRed: {
  //   nameKey: "edit.general.theme.palettes.powerfitRed.name",
  //   descriptionKey: "edit.general.theme.palettes.powerfitRed.description",
  //   previewColors: [
  //     "from-red-600 to-rose-700",
  //     "from-rose-600 to-pink-700",
  //     "from-red-500 to-red-600",
  //   ],
  //   hoverColor: "red",
  // },
  // studioXNeon: {
  //   nameKey: "edit.general.theme.palettes.studioXNeon.name",
  //   descriptionKey: "edit.general.theme.palettes.studioXNeon.description",
  //   previewColors: [
  //     "from-fuchsia-500 to-pink-600",
  //     "from-pink-600 to-purple-600",
  //     "from-purple-500 to-fuchsia-500",
  //   ],
  //   hoverColor: "fuchsia",
  // },
  // stylehubAmber: {
  //   nameKey: "edit.general.theme.palettes.stylehubAmber.name",
  //   descriptionKey: "edit.general.theme.palettes.stylehubAmber.description",
  //   previewColors: [
  //     "from-amber-600 to-orange-600",
  //     "from-orange-600 to-red-600",
  //     "from-amber-500 to-amber-600",
  //   ],
  //   hoverColor: "amber",
  // },
  // techsummitVibrant: {
  //   nameKey: "edit.general.theme.palettes.techsummitVibrant.name",
  //   descriptionKey: "edit.general.theme.palettes.techsummitVibrant.description",
  //   previewColors: [
  //     "from-blue-600 to-purple-600",
  //     "from-purple-600 to-pink-600",
  //     "from-cyan-500 to-blue-600",
  //   ],
  //   hoverColor: "blue",
  // },
  // New Tailwind color palettes metadata
  tailwindAmber: {
    nameKey: "edit.general.theme.palettes.tailwindAmber.name",
    descriptionKey: "edit.general.theme.palettes.tailwindAmber.description",
    previewColors: [
      "from-amber-600 to-amber-700",
      "from-amber-700 to-amber-900",
      "from-amber-500 to-orange-500",
    ],
    hoverColor: "amber",
  },
  tailwindBlue: {
    nameKey: "edit.general.theme.palettes.tailwindBlue.name",
    descriptionKey: "edit.general.theme.palettes.tailwindBlue.description",
    previewColors: [
      "from-blue-600 to-blue-700",
      "from-blue-700 to-blue-900",
      "from-blue-500 to-indigo-500",
    ],
    hoverColor: "blue",
  },
  tailwindCyan: {
    nameKey: "edit.general.theme.palettes.tailwindCyan.name",
    descriptionKey: "edit.general.theme.palettes.tailwindCyan.description",
    previewColors: [
      "from-cyan-600 to-cyan-700",
      "from-cyan-700 to-cyan-900",
      "from-cyan-500 to-blue-500",
    ],
    hoverColor: "cyan",
  },
  tailwindEmerald: {
    nameKey: "edit.general.theme.palettes.tailwindEmerald.name",
    descriptionKey: "edit.general.theme.palettes.tailwindEmerald.description",
    previewColors: [
      "from-emerald-600 to-emerald-700",
      "from-emerald-700 to-emerald-900",
      "from-emerald-500 to-green-500",
    ],
    hoverColor: "emerald",
  },
  tailwindFuchsia: {
    nameKey: "edit.general.theme.palettes.tailwindFuchsia.name",
    descriptionKey: "edit.general.theme.palettes.tailwindFuchsia.description",
    previewColors: [
      "from-fuchsia-600 to-fuchsia-700",
      "from-fuchsia-700 to-fuchsia-900",
      "from-fuchsia-500 to-pink-500",
    ],
    hoverColor: "fuchsia",
  },
  tailwindGray: {
    nameKey: "edit.general.theme.palettes.tailwindGray.name",
    descriptionKey: "edit.general.theme.palettes.tailwindGray.description",
    previewColors: [
      "from-gray-600 to-gray-700",
      "from-gray-700 to-gray-900",
      "from-gray-500 to-slate-500",
    ],
    hoverColor: "gray",
  },
  tailwindGreen: {
    nameKey: "edit.general.theme.palettes.tailwindGreen.name",
    descriptionKey: "edit.general.theme.palettes.tailwindGreen.description",
    previewColors: [
      "from-green-600 to-green-700",
      "from-green-700 to-green-900",
      "from-green-500 to-emerald-500",
    ],
    hoverColor: "green",
  },
  tailwindIndigo: {
    nameKey: "edit.general.theme.palettes.tailwindIndigo.name",
    descriptionKey: "edit.general.theme.palettes.tailwindIndigo.description",
    previewColors: [
      "from-indigo-600 to-indigo-700",
      "from-indigo-700 to-indigo-900",
      "from-indigo-500 to-purple-500",
    ],
    hoverColor: "indigo",
  },
  tailwindLime: {
    nameKey: "edit.general.theme.palettes.tailwindLime.name",
    descriptionKey: "edit.general.theme.palettes.tailwindLime.description",
    previewColors: [
      "from-lime-600 to-lime-700",
      "from-lime-700 to-lime-900",
      "from-lime-500 to-green-500",
    ],
    hoverColor: "lime",
  },
  tailwindNeutral: {
    nameKey: "edit.general.theme.palettes.tailwindNeutral.name",
    descriptionKey: "edit.general.theme.palettes.tailwindNeutral.description",
    previewColors: [
      "from-neutral-600 to-neutral-700",
      "from-neutral-700 to-neutral-900",
      "from-neutral-500 to-gray-500",
    ],
    hoverColor: "neutral",
  },
  tailwindOrange: {
    nameKey: "edit.general.theme.palettes.tailwindOrange.name",
    descriptionKey: "edit.general.theme.palettes.tailwindOrange.description",
    previewColors: [
      "from-orange-600 to-orange-700",
      "from-orange-700 to-orange-900",
      "from-orange-500 to-red-500",
    ],
    hoverColor: "orange",
  },
  tailwindPink: {
    nameKey: "edit.general.theme.palettes.tailwindPink.name",
    descriptionKey: "edit.general.theme.palettes.tailwindPink.description",
    previewColors: [
      "from-pink-600 to-pink-700",
      "from-pink-700 to-pink-900",
      "from-pink-500 to-rose-500",
    ],
    hoverColor: "pink",
  },
  tailwindPurple: {
    nameKey: "edit.general.theme.palettes.tailwindPurple.name",
    descriptionKey: "edit.general.theme.palettes.tailwindPurple.description",
    previewColors: [
      "from-purple-600 to-purple-700",
      "from-purple-700 to-purple-900",
      "from-purple-500 to-violet-500",
    ],
    hoverColor: "purple",
  },
  tailwindRed: {
    nameKey: "edit.general.theme.palettes.tailwindRed.name",
    descriptionKey: "edit.general.theme.palettes.tailwindRed.description",
    previewColors: [
      "from-red-600 to-red-700",
      "from-red-700 to-red-900",
      "from-red-500 to-rose-500",
    ],
    hoverColor: "red",
  },
  tailwindRose: {
    nameKey: "edit.general.theme.palettes.tailwindRose.name",
    descriptionKey: "edit.general.theme.palettes.tailwindRose.description",
    previewColors: [
      "from-rose-600 to-rose-700",
      "from-rose-700 to-rose-900",
      "from-rose-500 to-pink-500",
    ],
    hoverColor: "rose",
  },
  tailwindSky: {
    nameKey: "edit.general.theme.palettes.tailwindSky.name",
    descriptionKey: "edit.general.theme.palettes.tailwindSky.description",
    previewColors: [
      "from-sky-600 to-sky-700",
      "from-sky-700 to-sky-900",
      "from-sky-500 to-blue-500",
    ],
    hoverColor: "sky",
  },
  tailwindSlate: {
    nameKey: "edit.general.theme.palettes.tailwindSlate.name",
    descriptionKey: "edit.general.theme.palettes.tailwindSlate.description",
    previewColors: [
      "from-slate-600 to-slate-700",
      "from-slate-700 to-slate-900",
      "from-slate-500 to-gray-500",
    ],
    hoverColor: "slate",
  },
  tailwindStone: {
    nameKey: "edit.general.theme.palettes.tailwindStone.name",
    descriptionKey: "edit.general.theme.palettes.tailwindStone.description",
    previewColors: [
      "from-stone-600 to-stone-700",
      "from-stone-700 to-stone-900",
      "from-stone-500 to-gray-500",
    ],
    hoverColor: "stone",
  },
  tailwindTeal: {
    nameKey: "edit.general.theme.palettes.tailwindTeal.name",
    descriptionKey: "edit.general.theme.palettes.tailwindTeal.description",
    previewColors: [
      "from-teal-600 to-teal-700",
      "from-teal-700 to-teal-900",
      "from-teal-500 to-cyan-500",
    ],
    hoverColor: "teal",
  },
  tailwindViolet: {
    nameKey: "edit.general.theme.palettes.tailwindViolet.name",
    descriptionKey: "edit.general.theme.palettes.tailwindViolet.description",
    previewColors: [
      "from-violet-600 to-violet-700",
      "from-violet-700 to-violet-900",
      "from-violet-500 to-purple-500",
    ],
    hoverColor: "violet",
  },
  tailwindYellow: {
    nameKey: "edit.general.theme.palettes.tailwindYellow.name",
    descriptionKey: "edit.general.theme.palettes.tailwindYellow.description",
    previewColors: [
      "from-yellow-600 to-yellow-700",
      "from-yellow-700 to-yellow-900",
      "from-yellow-500 to-amber-500",
    ],
    hoverColor: "yellow",
  },
  tailwindZinc: {
    nameKey: "edit.general.theme.palettes.tailwindZinc.name",
    descriptionKey: "edit.general.theme.palettes.tailwindZinc.description",
    previewColors: [
      "from-zinc-600 to-zinc-700",
      "from-zinc-700 to-zinc-900",
      "from-zinc-500 to-gray-500",
    ],
    hoverColor: "zinc",
  },
};
